/* eslint-disable max-lines */
import moment from 'moment'
import { THROW_INTERVAL_ERROR_MESSAGE } from '../constants/error'

/**
 * @deprecated Please use DateInterval instead
 * The resulting interval object. Attention: This is a custom interval and
 * not the interval defined by ISO 8601 durations.
 *
 * @see {@link https://en.wikipedia.org/wiki/ISO_8601#Time_intervals}
 *
 * @typedef {Object} Interval
 * @property {moment.Moment} start - Start of the period
 * @property {moment.Moment} end - End of the period
 */

/**
 * @deprecated Please use DateInterval instead.
 *
 * @typedef {Object} MixedInterval
 * @property {moment.Moment|Date} start - Start of the period
 * @property {moment.Moment|Date} end - End of the period
 */

/**
 * The possible duration types for manipulating dates. Just exported for unit tests.
 *
 * Currently the typescript definition and documentation of moment is wrong. IDEA complains about using 'isoWeek' in subtract, duration and add
 * but it is possible. So we have to live with this warnings (see {@link getLastWeek()}).
 *
 * @private
 * @typedef {('seconds'|'minutes'|'hours'|'days'|'weeks'|'isoWeek'|'months'|'years')} durationPeriod
 */
const durationPeriod = {
    DAYS: 'days',
    HOURS: 'hours',
    ISO_WEEK: 'isoWeek',
    MINUTES: 'minutes',
    MONTHS: 'months',
    SECONDS: 'seconds',
    WEEKS: 'weeks',
    YEARS: 'years'
}

/**
 * Creates an interval object with a start and an end date, adjusts the start and end of the day.
 *
 * @param {moment.Moment|Date|string} start - A moment or Date object representing the start time
 * @param {moment.Moment|Date|string} end - A moment or Date object representing the end time
 * @param {durationPeriod} [period='days'] - A period to specify the difference
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getDatesIntervalObject(start, end, period = durationPeriod.DAYS) {
    return {
        end: moment(end).endOf(period),
        start: moment(start).startOf(period)
    }
}

/**
 * Creates an interval which defines the start and end date as moment object, creating a
 * period which is in the future. Just exported for unit tests.
 *
 * @private
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @param {durationPeriod} [period='months'] - A period to specify the difference
 * @param {number} [duration=0] - A duration which will be added, can also be negative
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNext(current = moment(), period = durationPeriod.MONTHS, duration = 1) {
    const start = moment(current).add(1, period).startOf(period)
    const end = moment(current).add(duration, period).endOf(period)

    return getDatesIntervalObject(start, end)
}

/**
 * Creates an interval which defines the start and end date as moment object, defining a
 * period which is in the future. The start is the current given time. Just exported for
 * unit tests.
 *
 * @private
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @param {durationPeriod} [period='months'] - A period to specify the difference
 * @param {number} [duration=0] - A duration which will be added, can also be negative
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNextFromNow(current = moment(), period = durationPeriod.MONTHS, duration = 0) {
    const start = moment(current)

    return getDatesIntervalObject(start, moment(start).add(moment.duration(duration, period)))
}

/**
 * Creates an interval which defines the start and end date as moment object, defining a
 * period which is in the future. Just exported for unit tests.
 *
 * @private
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @param {durationPeriod} [period='months'] - A period to specify the difference
 * @param {number} [duration=0] - A duration which will be added, can also be negative
 * @param {durationPeriod} [periodOf] - Defining start/end of range, will fall back to period if undefined
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLast(current = moment(), period = durationPeriod.MONTHS, duration = 1, periodOf) {
    const startOf = periodOf || period
    const endOf = periodOf || period
    const start = moment(current).subtract(duration, period).startOf(startOf)
    const end = moment(current).subtract(1, period).endOf(endOf)

    return getDatesIntervalObject(start, end)
}

/**
 * Creates an interval which defines the start and end date as moment object, defining a
 * period which is in the past. Just exported for unit tests.
 *
 * @private
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @param {durationPeriod} [period='days'] - A period to specify the difference
 * @param {number} [duration=0] - A duration which will be added, can also be negative
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLastTillNow(current = moment(), period = durationPeriod.DAYS, duration = 0) {
    const end = moment(current)

    return getDatesIntervalObject(moment(end).subtract(moment.duration(duration, period)), end)
}

/**
 * Creates an interval which defines the start and end date as moment object for one day in the future.
 * Just exported for unit tests.
 *
 * @private
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @param {number} [duration=1] - A duration which will be added, can also be negative
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getDayDaysLater(current = moment(), duration = 1) {
    const start = moment(current).add(duration, durationPeriod.DAYS).startOf(durationPeriod.DAYS)
    const end = moment(current).add(duration, durationPeriod.DAYS).endOf(durationPeriod.DAYS)

    return getDatesIntervalObject(start, end)
}

/**
 * Creates an interval which defines the start and end date as moment object for one day in the past.
 * Just exported for unit tests.
 *
 * @private
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @param {number} [duration=1] - A duration which will be added, can also be negative
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getDayDaysBefore(current = moment(), duration = 1) {
    const start = moment(current).subtract(duration, durationPeriod.DAYS).startOf(durationPeriod.DAYS)
    const end = moment(current).subtract(duration, durationPeriod.DAYS).endOf(durationPeriod.DAYS)

    return getDatesIntervalObject(start, end)
}

/**
 * Get an interval for the next 2 years.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNext2Years(current = moment()) {
    return getNext(current, durationPeriod.YEARS, 2)
}

/**
 * Get an interval for the next 2 years, starting from current timestamp.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNext2YearsFromNow(current = moment()) {
    return getNextFromNow(current, durationPeriod.YEARS, 2)
}

/**
 * Get an interval for the next year.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNextYear(current = moment()) {
    return getNext(current, durationPeriod.YEARS, 1)
}

/**
 * Get an interval for the next year, starting from current timestamp.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNextYearFromNow(current = moment()) {
    return getNextFromNow(current, durationPeriod.YEARS, 1)
}

/**
 * Get an interval for the next 6 months.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNext6Month(current = moment()) {
    return getNext(current, durationPeriod.MONTHS, 6)
}

/**
 * Get an interval for the next 6 months, starting from current timestamp.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNext6MonthFromNow(current = moment()) {
    return getNextFromNow(current, durationPeriod.MONTHS, 6)
}

/**
 * Get an interval for the next 3 months.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNext3Month(current = moment()) {
    return getNext(current, durationPeriod.MONTHS, 3)
}

/**
 * Get an interval for the next 3 months, starting from current timestamp.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNext3MonthFromNow(current = moment()) {
    return getNextFromNow(current, durationPeriod.MONTHS, 3)
}

/**
 * Get an interval for the next month.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNextMonth(current = moment()) {
    return getNext(current, durationPeriod.MONTHS, 1)
}

/**
 * Get an interval for the next month, starting from current timestamp.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNextMonthFromNow(current = moment()) {
    return getNextFromNow(current, durationPeriod.MONTHS, 1)
}

/**
 * Get an interval for the next 6 weeks.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNext6Weeks(current = moment()) {
    return getNext(current, durationPeriod.WEEKS, 6)
}

/**
 * Get an interval for the next 6 weeks, starting from current timestamp.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNext6WeeksFromNow(current = moment()) {
    return getNextFromNow(current, durationPeriod.WEEKS, 6)
}

/**
 * Get an interval for the next 3 weeks.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNext3Weeks(current = moment()) {
    return getNext(current, durationPeriod.WEEKS, 3)
}

/**
 * Get an interval for the next 3 weeks, starting from current timestamp.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNext3WeeksFromNow(current = moment()) {
    return getNextFromNow(current, durationPeriod.WEEKS, 3)
}

/**
 * Get an interval for the next week.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNextWeek(current = moment()) {
    return getNext(current, durationPeriod.WEEKS, 1)
}

/**
 * Get an interval for the next week, starting from current timestamp.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNextWeekFromNow(current = moment()) {
    return getNextFromNow(current, durationPeriod.WEEKS, 1)
}

/**
 * Get an interval for the next 30 days, starting from current timestamp.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNext30Days(current = moment()) {
    return getNextFromNow(current, durationPeriod.DAYS, 30)
}

/**
 * Get an interval for the next 7 days, starting from current timestamp.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getNext7Days(current = moment()) {
    return getNextFromNow(current, durationPeriod.DAYS, 7)
}

/**
 * Get an interval for the day 6 days later than today.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getDay6DaysLater(current = moment()) {
    return getDayDaysLater(current, 6)
}

/**
 * Get an interval for the day 5 days later than today.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getDay5DaysLater(current = moment()) {
    return getDayDaysLater(current, 5)
}

/**
 * Get an interval for the day 4 days later than today.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getDay4DaysLater(current = moment()) {
    return getDayDaysLater(current, 4)
}

/**
 * Get an interval for the day 3 days later than today.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getDay3DaysLater(current = moment()) {
    return getDayDaysLater(current, 3)
}

/**
 * Get an interval for the day 2 days later than today.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getDay2DaysLater(current = moment()) {
    return getDayDaysLater(current, 2)
}

/**
 * Get an interval for tomorrow.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getTomorrow(current = moment()) {
    return getDayDaysLater(current, 1)
}

/**
 * Get an interval for the whole current year, where the current date is included.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getThisYear(current = moment()) {
    const startThisYear = moment(current).startOf(durationPeriod.YEARS)
    const endThisYear = moment(current).endOf(durationPeriod.YEARS)

    return getDatesIntervalObject(startThisYear, endThisYear)
}

/**
 * Get an interval for the whole current month, where the current date is included.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getThisMonth(current = moment()) {
    const startThisMonth = moment(current).startOf(durationPeriod.MONTHS)
    const endThisMonth = moment(current).endOf(durationPeriod.MONTHS)

    return getDatesIntervalObject(startThisMonth, endThisMonth)
}

/**
 * Gets an interval for the whole current week, where the current date is included.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getThisWeek(current = moment()) {
    const startThisWeek = moment(current).weekday(0)
    const endThisWeek = moment(current).weekday(6)

    return getDatesIntervalObject(startThisWeek, endThisWeek)
}

/**
 * Get an interval for the current day.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getToday(current = moment()) {
    return getLastTillNow(current, durationPeriod.DAYS, 0)
}

/**
 * Get an interval for yesterday.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects.
 */
function getYesterday(current = moment()) {
    const yesterday = moment(current).subtract(1, durationPeriod.DAYS)
    return getDatesIntervalObject(yesterday, yesterday)
}

/**
 * Get an interval for the day 2 days before today.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getDay2DaysBefore(current = moment()) {
    return getDayDaysBefore(current, 2)
}

/**
 * Get an interval for the day 3 days before today.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getDay3DaysBefore(current = moment()) {
    return getDayDaysBefore(current, 3)
}

/**
 * Get an interval for the day 4 days before today.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getDay4DaysBefore(current = moment()) {
    return getDayDaysBefore(current, 4)
}

/**
 * Get an interval for the day 5 days before today.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getDay5DaysBefore(current = moment()) {
    return getDayDaysBefore(current, 5)
}

/**
 * Get an interval for the day 6 days before today.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getDay6DaysBefore(current = moment()) {
    return getDayDaysBefore(current, 6)
}

/**
 * Get an interval for the last 6 days started by today as last day.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLast6Days(current = moment()) {
    return getLastTillNow(current, durationPeriod.DAYS, 6)
}

/**
 * Get an interval for the last 7 days started by today as last day.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLast7Days(current = moment()) {
    return getLastTillNow(current, durationPeriod.DAYS, 7)
}

/**
 * Get an interval for the last 30 days started by today as last day.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLast30Days(current = moment()) {
    return getLastTillNow(current, durationPeriod.DAYS, 30)
}

/**
 * Get an interval for the last 7 days until tomorrow.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLast7DaysAndTomorrow(current = moment()) {
    const start = getLast7Days(current).start
    const end = getTomorrow(current).end

    return {
        end,
        start
    }
}

/**
 * Get an interval for the last 90 days plus tomorrow, useful for Rest Filters where "to" is exclusive
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLast90DaysAndTomorrow(current = moment()) {
    const start = getLastTillNow(current, durationPeriod.DAYS, 90).start
    const end = getTomorrow(current).end

    return {
        end,
        start
    }
}

/**
 * Get an interval for the whole last week.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLastWeek(current = moment()) {
    return getLast(current, durationPeriod.WEEKS, 1, durationPeriod.ISO_WEEK)
}

/**
 * Get an interval for the last week by today as last day.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLastWeekTillNow(current = moment()) {
    return getLastTillNow(current, durationPeriod.WEEKS, 1)
}

/**
 * Get an interval for the last 3 weeks.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLast3Weeks(current = moment()) {
    const start = moment(current).subtract(3, durationPeriod.WEEKS).startOf('isoWeek')
    const end = moment(current).subtract(1, durationPeriod.WEEKS).endOf('isoWeek')

    return getDatesIntervalObject(start, end)
}

/**
 * Get an interval for the last 3 weeks by today as last day.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLast3WeeksTillNow(current = moment()) {
    return getLastTillNow(current, durationPeriod.WEEKS, 3)
}

/**
 * Get an interval for the last 6 weeks.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLast6Weeks(current = moment()) {
    const start = moment(current).subtract(6, durationPeriod.WEEKS).startOf('isoWeek')
    const end = moment(current).subtract(1, durationPeriod.WEEKS).endOf('isoWeek')

    return getDatesIntervalObject(start, end)
}

/**
 * Get an interval for the last 6 weeks by today as last day.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLast6WeeksTillNow(current = moment()) {
    return getLastTillNow(current, durationPeriod.WEEKS, 6)
}

/**
 * Get an interval for the whole last month.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLastMonth(current = moment()) {
    return getLast(current, durationPeriod.MONTHS, 1)
}

/**
 * Get an interval for the whole last month by today as last day.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLastMonthTillNow(current = moment()) {
    return getLastTillNow(current, durationPeriod.MONTHS, 1)
}

/**
 * Get an interval for the last 3 month.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLast3Month(current = moment()) {
    return getLast(current, durationPeriod.MONTHS, 3)
}

/**
 * Get an interval for the last 3 month by today as last day.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLast3MonthTillNow(current = moment()) {
    return getLastTillNow(current, durationPeriod.MONTHS, 3)
}

/**
 * Get an interval for the last 6 month.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLast6Month(current = moment()) {
    return getLast(current, durationPeriod.MONTHS, 6)
}

/**
 * Get an interval for the last 6 month by today as last day.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLast6MonthTillNow(current = moment()) {
    return getLastTillNow(current, durationPeriod.MONTHS, 6)
}

/**
 * Get an interval for the last year.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLastYear(current = moment()) {
    return getLast(current, durationPeriod.YEARS, 1)
}

/**
 * Get an interval for the last year by today as last day.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLastYearTillNow(current = moment()) {
    return getLastTillNow(current, durationPeriod.YEARS, 1)
}

/**
 * Get an interval for the last 2 years.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLast2Years(current = moment()) {
    return getLast(current, durationPeriod.YEARS, 2)
}

/**
 * Gets an interval for the last 2 years by today as last day.
 *
 * @param {moment.Moment|Date|string} [current=moment()] - A moment or Date object (defaults to `now`)
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getLast2YearsTillNow(current = moment()) {
    return getLastTillNow(current, durationPeriod.YEARS, 2)
}

/**
 * Get the platform's default interval.
 *
 * @returns {Interval} The interval as object contains start and end moment objects
 */
function getDefaultInterval() {
    return getLast7Days()
}

/**
 * Convert an interval Object to one that consists of Date objects instead of moment() objects.
 *
 * @param {Interval} interval - The interval to be converted
 * @returns {Object} The object containing dates
 */
function intervalToDate(interval) {
    if (!interval || !interval.start || !interval.end) {
        throw new Error(THROW_INTERVAL_ERROR_MESSAGE)
    }

    return {
        end: interval.end.toDate(),
        start: interval.start.toDate()
    }
}

export {
    durationPeriod,
    getDatesIntervalObject,
    getDay2DaysBefore,
    getDay2DaysLater,
    getDay3DaysBefore,
    getDay3DaysLater,
    getDay4DaysBefore,
    getDay4DaysLater,
    getDay5DaysBefore,
    getDay5DaysLater,
    getDay6DaysBefore,
    getDay6DaysLater,
    getDayDaysBefore,
    getDayDaysLater,
    getDefaultInterval,
    getLast,
    getLast2Years,
    getLast2YearsTillNow,
    getLast3Month,
    getLast3MonthTillNow,
    getLast3Weeks,
    getLast3WeeksTillNow,
    getLast6Days,
    getLast6Month,
    getLast6MonthTillNow,
    getLast6Weeks,
    getLast6WeeksTillNow,
    getLast7Days,
    getLast7DaysAndTomorrow,
    getLast30Days,
    getLast90DaysAndTomorrow,
    getLastMonth,
    getLastMonthTillNow,
    getLastTillNow,
    getLastWeek,
    getLastWeekTillNow,
    getLastYear,
    getLastYearTillNow,
    getNext,
    getNext2Years,
    getNext2YearsFromNow,
    getNext3Month,
    getNext3MonthFromNow,
    getNext3Weeks,
    getNext3WeeksFromNow,
    getNext6Month,
    getNext6MonthFromNow,
    getNext6Weeks,
    getNext6WeeksFromNow,
    getNext7Days,
    getNext30Days,
    getNextFromNow,
    getNextMonth,
    getNextMonthFromNow,
    getNextWeek,
    getNextWeekFromNow,
    getNextYear,
    getNextYearFromNow,
    getThisMonth,
    getThisWeek,
    getThisYear,
    getToday,
    getTomorrow,
    getYesterday,
    intervalToDate
}
